import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import cleanDeep from 'clean-deep';
import { useContext } from 'react';
import Maybe from 'true-myth/maybe';

import { ApplicationContext } from '~/shared/context/context';
import type {
    EducationHistoryCreation,
    EducationHistoryItem,
    EducationHistoryMutation,
} from '~/shared/data-contract/education-history';
import { educationHistoryItemArraySchema, educationHistoryItemSchema } from '~/shared/data-contract/education-history';
import { getUserHardSoftSkillsQueryKey } from '~/shared/query-hooks/hard-soft-skills';

import { nullifyEmptyStringValues } from '../../common/form';

export const useEducationHistoryQuery = (
    userId: string,
    enabled: boolean,
): UseQueryResult<readonly EducationHistoryItem[]> => {
    const { validatingCobrainerApiFetcher } = useContext(ApplicationContext);
    const { applicationState } = useContext(ApplicationContext);
    const { language } = applicationState;
    const educationHistoryUrl = `/users/${userId}/education-history` as const;

    return useQuery({
        enabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(educationHistoryItemArraySchema, {
                pathname: educationHistoryUrl,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [educationHistoryUrl, 'GET', language.code],
    });
};

export const useOneEducationHistoryQuery = (
    userId: string,
    educationHistoryId: string,
    enabled: boolean,
): UseQueryResult<EducationHistoryItem> => {
    const {
        applicationState: { language },
        validatingCobrainerApiFetcher,
    } = useContext(ApplicationContext);
    const oneEducationHistoryUrl = `/users/${userId}/education-history/${educationHistoryId}` as const;

    return useQuery({
        enabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(educationHistoryItemSchema, {
                pathname: oneEducationHistoryUrl,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: [oneEducationHistoryUrl, 'GET', language.code],
    });
};

export type EducationHistoryMutations = {
    readonly createEducationMutation: UseMutationResult<
        EducationHistoryItem,
        unknown,
        Omit<EducationHistoryItem, 'id'>
    >;
    readonly deleteEducationMutation: UseMutationResult<void, unknown, string>;
    readonly patchEducationMutation: UseMutationResult<EducationHistoryItem, unknown, EducationHistoryItem>;
};

export const useEducationHistoryMutations = (userId: string): EducationHistoryMutations => {
    const queryClient = useQueryClient();
    const { cobrainerApiFetcher, validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    const invalidateEducationCache = async (educationHistoryId: Maybe<string>): Promise<void> => {
        if (educationHistoryId.isJust) {
            await queryClient.invalidateQueries({
                queryKey: [`/users/${userId}/education-history/${educationHistoryId}`, 'GET'],
            });
        }
        await queryClient.invalidateQueries({ queryKey: [`/users/${userId}/education-history`, 'GET'] });
        await queryClient.invalidateQueries({ queryKey: getUserHardSoftSkillsQueryKey(userId) });
    };

    const createEducationMutation = useMutation({
        mutationFn: async (values: Omit<EducationHistoryItem, 'id'>): Promise<EducationHistoryItem> => {
            const { degree, fieldOfStudy, schoolName, skills, ...restValues } = values;
            const data: EducationHistoryCreation = {
                ...cleanDeep(restValues),
                degree,
                fieldOfStudy,
                schoolName,
                skillIds: skills?.map((skill) => skill.id) ?? [],
            };

            const result = await validatingCobrainerApiFetcher.postJson(educationHistoryItemSchema, {
                pathname: `/users/${userId}/education-history`,
                payload: data,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onSuccess: (values: EducationHistoryItem) => invalidateEducationCache(Maybe.just(values.id)),
    });

    const patchEducationMutation = useMutation({
        mutationFn: async (values: EducationHistoryItem): Promise<EducationHistoryItem> => {
            const { id, degree, fieldOfStudy, schoolName, skills, ...restValues } = values;
            const data: EducationHistoryMutation = {
                ...cleanDeep(nullifyEmptyStringValues(restValues), { nullValues: false }),
                id,
                degree,
                fieldOfStudy,
                schoolName,
                skillIds: skills?.map((skill) => skill.id) ?? [],
            };

            const result = await validatingCobrainerApiFetcher.patchJson(educationHistoryItemSchema, {
                pathname: `/users/${userId}/education-history/${values.id}`,
                payload: data,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onSuccess: (_, values) => invalidateEducationCache(Maybe.just(values.id)),
    });

    const deleteEducationMutation: UseMutationResult<void, unknown, string> = useMutation({
        mutationFn: async (id: string) => {
            const result = await cobrainerApiFetcher.delete({
                pathname: `/users/${userId}/education-history/${id}`,
            });

            if (result.isOk) {
                return;
            }

            throw result.error;
        },
        onSuccess: () => invalidateEducationCache(Maybe.nothing()),
    });

    return {
        createEducationMutation,
        deleteEducationMutation,
        patchEducationMutation,
    };
};
